/**
 * use-training.ts — Đào tạo nội bộ: cây thư mục + tài liệu Markdown có phiên bản.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'

export interface TrainingFolder { id: string; parentId: string | null; name: string; slug: string; description: string | null; sortOrder: number }
export interface TrainingDocMeta { id: string; folderId: string | null; slug: string; title: string; sortOrder: number; version: number; updatedAt: string }
export interface TrainingTree { folders: TrainingFolder[]; docs: TrainingDocMeta[]; canEdit: boolean; /** Số nhận xét đang mở theo id tài liệu. */ nhanXetMo?: Record<string, number> }
export interface TrainingDoc extends TrainingDocMeta {
  content: string
  updatedById: string | null
  createdAt: string
  folder: { id: string; name: string; parentId: string | null } | null
}
export interface TrainingVersion { id: string; version: number; title: string; changedBy: string | null; changeNote: string | null; createdAt: string }
export interface TrainingSearchHit { id: string; title: string; folderId: string | null; trich: string }

export const trainingKeys = {
  tree: ['training', 'tree'] as const,
  doc: (id: string) => ['training', 'doc', id] as const,
  versions: (id: string) => ['training', 'versions', id] as const,
  search: (q: string) => ['training', 'search', q] as const,
}

export function useTrainingTree() {
  return useQuery({ queryKey: trainingKeys.tree, queryFn: async () => (await api.get<TrainingTree>('/training/tree')).data })
}

export function useTrainingDoc(id: string | undefined) {
  return useQuery({
    queryKey: trainingKeys.doc(id ?? ''),
    enabled: !!id,
    queryFn: async () => (await api.get<{ doc: TrainingDoc }>(`/training/docs/${id}`)).data.doc,
  })
}

export function useTrainingVersions(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: trainingKeys.versions(id ?? ''),
    enabled: !!id && enabled,
    queryFn: async () => (await api.get<{ versions: TrainingVersion[] }>(`/training/docs/${id}/versions`)).data.versions,
  })
}

export function useTrainingSearch(q: string) {
  return useQuery({
    queryKey: trainingKeys.search(q),
    enabled: q.trim().length >= 2,
    queryFn: async () => (await api.get<{ results: TrainingSearchHit[] }>('/training/search', { params: { q } })).data.results,
  })
}

export function useSaveTrainingDoc() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id?: string; title?: string; content?: string; folderId?: string | null; sortOrder?: number; changeNote?: string }) => {
      const { id, ...body } = input
      const { data } = id
        ? await api.put<{ doc: TrainingDoc }>(`/training/docs/${id}`, body)
        : await api.post<{ doc: TrainingDoc }>('/training/docs', body)
      return data.doc
    },
    onSuccess: (doc) => {
      void qc.invalidateQueries({ queryKey: trainingKeys.tree })
      void qc.invalidateQueries({ queryKey: trainingKeys.doc(doc.id) })
      void qc.invalidateQueries({ queryKey: trainingKeys.versions(doc.id) })
    },
  })
}

export function useDeleteTrainingDoc() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/training/docs/${id}`)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: trainingKeys.tree }),
  })
}

export function useSaveTrainingFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { id?: string; name?: string; parentId?: string | null; description?: string | null; sortOrder?: number }) => {
      const { id, ...body } = input
      const { data } = id
        ? await api.patch<{ folder: TrainingFolder }>(`/training/folders/${id}`, body)
        : await api.post<{ folder: TrainingFolder }>('/training/folders', body)
      return data.folder
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: trainingKeys.tree }),
  })
}

export function useDeleteTrainingFolder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/training/folders/${id}`)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: trainingKeys.tree }),
  })
}

export async function fetchTrainingVersion(id: string, version: number) {
  const { data } = await api.get<{ version: TrainingVersion & { content: string } }>(`/training/docs/${id}/versions/${version}`)
  return data.version
}

export async function restoreTrainingVersion(id: string, version: number) {
  const { data } = await api.post<{ doc: TrainingDoc }>(`/training/docs/${id}/versions/${version}/restore`)
  return data.doc
}

// ── Nhận xét theo khối ───────────────────────────────────────────────
export interface NhanXet {
  id: string
  docId: string
  line: number
  quote: string
  body: string
  parentId: string | null
  status: 'open' | 'resolved'
  authorId: string
  authorName: string
  resolvedBy: string | null
  resolvedByName: string | null
  resolvedAt: string | null
  createdAt: string
  updatedAt: string
}

export function useNhanXet(docId: string | undefined) {
  return useQuery<{ comments: NhanXet[]; me: string; isAdmin: boolean }>({
    queryKey: ['training-comments', docId],
    queryFn: async () => (await api.get(`/training/docs/${docId}/comments`)).data,
    enabled: !!docId,
  })
}

export function useTaoNhanXet(docId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { line: number; quote: string; body: string; parentId?: string | null }) =>
      (await api.post(`/training/docs/${docId}/comments`, input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['training-comments', docId] })
      qc.invalidateQueries({ queryKey: trainingKeys.tree })
    },
  })
}

export function useSuaNhanXet(docId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; body?: string; status?: 'open' | 'resolved'; line?: number; quote?: string }) =>
      (await api.patch(`/training/comments/${id}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['training-comments', docId] })
      qc.invalidateQueries({ queryKey: trainingKeys.tree })
    },
  })
}

export function useXoaNhanXet(docId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/training/comments/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['training-comments', docId] })
      qc.invalidateQueries({ queryKey: trainingKeys.tree })
    },
  })
}
